import React from "react";
//#region types
import { ChartEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { RangedAmount } from "models/propTypes/commonPropTypes";
import { DashboardMainChartWrapperProps } from "models/propTypes/chartProps";
import { GraphConstants } from "utils/constants";
//#endregion
//#region components
import {
  ChartFrequencySelector,
  ChartToggle,
  ChartTypeSelector,
  ExpandableViewPanel,
  TitleWithAmounts,
  useChartContext,
} from "components/common";
import DashboardMainChart from "./dashboardMainChart";
import { ErrorBoundary } from "components/error";
//#endregion
import { DateHelper, EnumHelper, LocalizationHelperInstance, SizingHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const DashboardMainChartWrapper: React.FC<DashboardMainChartWrapperProps> = (props) => {
  const [amounts, setAmounts] = React.useState<RangedAmount[]>();
  const { chartTypes } = props;

  const chartContext = useChartContext();
  const smallScreen = SizingHelper.isSmallScreen();

  const onChartSuccessHandler = React.useCallback((data?: WintSuperkollAPIModel.ReportWidget, filter?: any) => {
    const currentChart = data?.data;

    if (currentChart?.length) {
      const dataLength = currentChart[0].dataPoints?.length ?? 0;

      if (filter.type === ChartEnums.ChartDataFrequency.Quarterly) {
        if (data?.dataLabels?.length) {
          const currentYear = Number(data.dataLabels[data.dataLabels.length - 1].slice(-4));

          const titleAmounts = [
            {
              dateRange: {
                start: DateHelper.getFirstDayOfYear(currentYear),
                end: DateHelper.getLastDayOfYear(currentYear),
              },
              amount:
                data?.type === WintSuperkollAPIModel.ReportWidgetType.Bar
                  ? currentChart[0].dataPoints
                      ?.slice(-4)
                      ?.map((i) => i ?? 0)
                      .reduce((prev: number, current: number) => prev + current, 0) ?? 0
                  : currentChart[0].dataPoints?.length
                  ? currentChart[0].dataPoints[dataLength - 1]
                  : 0,
            },
          ];

          if (data.dataLabels.length > 4) {
            const previousYear = Number(data.dataLabels[data.dataLabels.length - 5].slice(-4));
            titleAmounts.push({
              dateRange: {
                start: DateHelper.getFirstDayOfYear(previousYear),
                end: DateHelper.getLastDayOfYear(previousYear),
              },
              amount:
                data?.type === WintSuperkollAPIModel.ReportWidgetType.Bar
                  ? currentChart[0].dataPoints
                      ?.slice(-8, -4)
                      ?.map((i) => i ?? 0)
                      .reduce((prev: number, current: number) => prev + current, 0) ?? 0
                  : currentChart[0].dataPoints?.length
                  ? currentChart[0].dataPoints[dataLength - 5]
                  : 0,
            });
          }
          setAmounts(titleAmounts);
        } else {
          setAmounts([]);
        }
      } else if (filter.type === ChartEnums.ChartDataFrequency.Yearly) {
        if (data?.dataLabels?.length) {
          const currentYear = Number(data.dataLabels[data.dataLabels.length - 1]);

          const titleAmounts = [
            {
              dateRange: {
                start: DateHelper.getFirstDayOfYear(currentYear),
                end: DateHelper.getLastDayOfYear(currentYear),
              },
              amount:
                data?.type === WintSuperkollAPIModel.ReportWidgetType.Bar
                  ? currentChart[0].dataPoints?.length
                    ? currentChart[0].dataPoints[dataLength - 1]
                    : 0
                  : currentChart[0].dataPoints?.length
                  ? currentChart[0].dataPoints[dataLength - 1] - currentChart[0].dataPoints[dataLength - 2]
                  : 0,
            },
          ];

          if (data.dataLabels.length > 1) {
            const previousYear = Number(data.dataLabels[data.dataLabels.length - 2]);
            titleAmounts.push({
              dateRange: {
                start: DateHelper.getFirstDayOfYear(previousYear),
                end: DateHelper.getLastDayOfYear(previousYear),
              },
              amount:
                data?.type === WintSuperkollAPIModel.ReportWidgetType.Bar
                  ? currentChart[0].dataPoints?.length
                    ? currentChart[0].dataPoints[dataLength - 2]
                    : 0
                  : currentChart[0].dataPoints?.length
                  ? currentChart[0].dataPoints[dataLength - 2] - currentChart[0].dataPoints[dataLength - 3]
                  : 0,
            });
          }

          setAmounts(titleAmounts);
        } else {
          setAmounts([]);
        }
      } else {
        setAmounts(
          currentChart.map((chart) => ({
            dateRange: { start: chart.startDate, end: DateHelper.addDays(chart.endDate, -1) },
            amount:
              data?.type === WintSuperkollAPIModel.ReportWidgetType.Bar
                ? chart.dataPoints?.map((i) => i ?? 0).reduce((prev: number, current: number) => prev + current, 0) ?? 0
                : chart.dataPoints?.length
                ? chart.dataPoints[chart.dataPoints?.length - 1]
                : 0,
          }))
        );
      }
    } else {
      setAmounts([]);
    }
  }, []);

  return (
    <ErrorBoundary>
      <ExpandableViewPanel
        title={
          <TitleWithAmounts
            title={
              chartContext?.filter?.chartType !== undefined
                ? LocalizationHelperInstance.getFormattedMessage(`Common.MainChart_${chartContext?.filter?.chartType}`)
                : ""
            }
            amounts={amounts ?? []}
          />
        }
        appearance="lg"
        gridSize="lg"
        isCanvasContent
        topRightContent={<ChartToggle />}
        footerLeftContent={<ChartFrequencySelector />}
        footerContainerId={GraphConstants.DashboardMainChartContainerId}
        footerRightContent={
          <ChartTypeSelector
            chartTypes={chartTypes ?? EnumHelper.getNumericItemValues(ChartEnums.MainDashBoardChartTypes)}
            localization={"Common.MainChart"}
          />
        }
        expanded={!smallScreen}
        className={cssExports.dashboardMainChartContainer}
      >
        <DashboardMainChart onChartSuccessHandler={onChartSuccessHandler} />
      </ExpandableViewPanel>
    </ErrorBoundary>
  );
};

export default DashboardMainChartWrapper;
